import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Lock, Plus, Pencil, Trash2, MoreVertical, Package, Inbox, LogOut, Image, Star, StarOff, Upload
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { value: "skeleton_trailers", label: "Skeleton Trailers" },
  { value: "flatbed_trailers", label: "Flatbed Trailers" },
  { value: "metals", label: "Metals" },
  { value: "cargo", label: "General Cargo" },
];

export default function AdminPage() {
  const [adminKey, setAdminKey] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Product form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState({
    title: "", category: "skeleton_trailers", condition: "new", description: "", images: [], featured: false,
  });
  const [imageUrl, setImageUrl] = useState("");

  const headers = { "x-admin-key": adminKey };

  const handleLogin = async () => {
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "x-admin-key": keyInput } });
      setAdminKey(keyInput);
      setAuthenticated(true);
      toast.success("Admin access granted");
    } catch {
      toast.error("Invalid admin key");
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, inqRes, conRes] = await Promise.all([
        axios.get(`${API}/products`, { params: { limit: 100 } }),
        axios.get(`${API}/admin/inquiries`, { headers }),
        axios.get(`${API}/admin/contact-inquiries`, { headers }),
      ]);
      setProducts(prodRes.data.products || []);
      setInquiries(inqRes.data || []);
      setContacts(conRes.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    if (authenticated) fetchData();
  }, [authenticated, fetchData]);

  const handleSeed = async () => {
    try {
      const res = await axios.post(`${API}/admin/seed`, {}, { headers });
      toast.success(res.data.message);
      fetchData();
    } catch {
      toast.error("Failed to seed data");
    }
  };

  const openCreateForm = () => {
    setEditingProduct(null);
    setForm({ title: "", category: "skeleton_trailers", condition: "new", description: "", images: [], featured: false });
    setImageUrl("");
    setFormOpen(true);
  };

  const openEditForm = (product) => {
    setEditingProduct(product);
    setForm({
      title: product.title,
      category: product.category,
      condition: product.condition,
      description: product.description || "",
      images: product.images || [],
      featured: product.featured || false,
    });
    setImageUrl("");
    setFormOpen(true);
  };

  const addImageUrl = () => {
    if (imageUrl.trim()) {
      setForm({ ...form, images: [...form.images, imageUrl.trim()] });
      setImageUrl("");
    }
  };

  const removeImage = (index) => {
    setForm({ ...form, images: form.images.filter((_, i) => i !== index) });
  };

  const handleSubmitProduct = async () => {
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, form, { headers });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, form, { headers });
        toast.success("Product created");
      }
      setFormOpen(false);
      fetchData();
    } catch {
      toast.error("Failed to save product");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers });
      toast.success("Product deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteInquiry = async (id) => {
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers });
      toast.success("Inquiry deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete inquiry");
    }
  };

  // Login Screen
  if (!authenticated) {
    return (
      <div data-testid="admin-login" className="pt-24 pb-20 flex items-center justify-center min-h-screen">
        <div className="w-full max-w-sm p-8 bg-[#0A0A12] border border-white/10 rounded-lg">
          <div className="flex items-center justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-[#DC143C]/10 border border-[#DC143C]/30 flex items-center justify-center">
              <Lock size={24} className="text-[#DC143C]" />
            </div>
          </div>
          <h2 className="text-white text-xl font-bold text-center mb-2">Admin Access</h2>
          <p className="text-[#9CA3AF] text-sm text-center mb-6">Enter admin key to manage products</p>
          <Input
            data-testid="admin-key-input"
            type="password"
            placeholder="Admin Key"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            className="bg-[#05050A] border-white/15 focus:border-[#DC143C] text-white mb-4"
          />
          <Button
            data-testid="admin-login-btn"
            onClick={handleLogin}
            className="w-full bg-[#DC143C] text-white hover:bg-[#B01030] font-semibold"
          >
            Access Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black tracking-tighter text-white">Admin Dashboard</h1>
            <p className="text-[#9CA3AF] text-sm mt-1">Manage products, inquiries, and messages</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="admin-seed-btn"
              onClick={handleSeed}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5"
            >
              Seed Data
            </Button>
            <Button
              data-testid="admin-logout-btn"
              onClick={() => { setAuthenticated(false); setAdminKey(""); }}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5"
            >
              <LogOut size={16} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 p-1 bg-[#0A0A12] border border-white/10 rounded-lg inline-flex">
          {[
            { id: "products", label: "Products", icon: Package, count: products.length },
            { id: "inquiries", label: "Inquiries", icon: Inbox, count: inquiries.length },
            { id: "contacts", label: "Messages", icon: Inbox, count: contacts.length },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === t.id ? "bg-[#DC143C] text-white" : "text-[#9CA3AF] hover:text-white"
              }`}
            >
              <t.icon size={16} />
              {t.label}
              <span className="text-xs opacity-70">({t.count})</span>
            </button>
          ))}
        </div>

        {/* Products Tab */}
        {tab === "products" && (
          <div>
            <div className="flex justify-end mb-4">
              <Button
                data-testid="admin-add-product-btn"
                onClick={openCreateForm}
                className="bg-[#DC143C] text-white hover:bg-[#B01030]"
              >
                <Plus size={16} className="mr-2" /> Add Product
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-[#9CA3AF]">Loading...</div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-[#9CA3AF] mb-4">No products yet</p>
                <Button onClick={handleSeed} className="bg-[#DC143C] text-white hover:bg-[#B01030]">
                  Seed Sample Data
                </Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-products-list">
                {products.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`admin-product-${p.id}`}
                    className="flex items-center gap-4 p-4 bg-[#0A0A12] border border-white/10 rounded-lg"
                  >
                    <div className="w-16 h-16 rounded bg-[#1E1E28] overflow-hidden shrink-0">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image size={20} className="text-[#9CA3AF]" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium text-sm truncate">{p.title}</span>
                        {p.featured && <Star size={14} className="text-[#DC143C] shrink-0" />}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-[#000080]/40 text-[#9CA3AF] border-[#7851A9]/30 text-[10px]">
                          {p.category}
                        </Badge>
                        <Badge className="bg-white/5 text-[#9CA3AF] border-white/10 text-[10px] capitalize">
                          {p.condition}
                        </Badge>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`admin-product-menu-${p.id}`}>
                          <MoreVertical size={16} className="text-[#9CA3AF]" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#0A0A12] border-white/10 text-white">
                        <DropdownMenuItem onClick={() => openEditForm(p)} className="cursor-pointer">
                          <Pencil size={14} className="mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(p.id)} className="cursor-pointer text-red-400">
                          <Trash2 size={14} className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Inquiries Tab */}
        {tab === "inquiries" && (
          <div className="space-y-3" data-testid="admin-inquiries-list">
            {inquiries.length === 0 ? (
              <div className="text-center py-12 text-[#9CA3AF]">No inquiries yet</div>
            ) : (
              inquiries.map((inq) => (
                <div
                  key={inq.id}
                  data-testid={`admin-inquiry-${inq.id}`}
                  className="p-5 bg-[#0A0A12] border border-white/10 rounded-lg"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span className="text-white font-medium text-sm">{inq.name}</span>
                      <span className="text-[#9CA3AF] text-xs ml-3">{inq.email}</span>
                      {inq.phone && <span className="text-[#9CA3AF] text-xs ml-3">{inq.phone}</span>}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteInquiry(inq.id)}>
                      <Trash2 size={14} className="text-[#9CA3AF]" />
                    </Button>
                  </div>
                  {inq.product_title && (
                    <p className="text-[#DC143C] text-xs font-medium mb-2">Re: {inq.product_title}</p>
                  )}
                  <p className="text-[#9CA3AF] text-sm">{inq.message}</p>
                  <p className="text-[#9CA3AF]/50 text-xs mt-2">
                    {new Date(inq.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {tab === "contacts" && (
          <div className="space-y-3" data-testid="admin-contacts-list">
            {contacts.length === 0 ? (
              <div className="text-center py-12 text-[#9CA3AF]">No contact messages yet</div>
            ) : (
              contacts.map((c) => (
                <div
                  key={c.id}
                  data-testid={`admin-contact-${c.id}`}
                  className="p-5 bg-[#0A0A12] border border-white/10 rounded-lg"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span className="text-white font-medium text-sm">{c.name}</span>
                      <span className="text-[#9CA3AF] text-xs ml-3">{c.email}</span>
                    </div>
                  </div>
                  {c.subject && <p className="text-[#DC143C] text-xs font-medium mb-2">{c.subject}</p>}
                  <p className="text-[#9CA3AF] text-sm">{c.message}</p>
                  <p className="text-[#9CA3AF]/50 text-xs mt-2">
                    {new Date(c.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Product Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="bg-[#0A0A12] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editingProduct ? "Edit Product" : "Add Product"}</DialogTitle>
            <DialogDescription className="text-[#9CA3AF]">
              {editingProduct ? "Update product details" : "Fill in the product details"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2">Title *</label>
              <Input
                data-testid="admin-product-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="bg-[#05050A] border-white/15 focus:border-[#DC143C] text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="admin-product-category" className="bg-[#05050A] border-white/15 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A12] border-white/10 text-white">
                    {CATEGORY_OPTIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2">Condition</label>
                <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                  <SelectTrigger data-testid="admin-product-condition" className="bg-[#05050A] border-white/15 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A12] border-white/10 text-white">
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="used">Used</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2">Description</label>
              <Textarea
                data-testid="admin-product-description"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#05050A] border-white/15 focus:border-[#DC143C] text-white resize-none"
              />
            </div>
            <div>
              <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2">Images</label>
              <div className="flex gap-2">
                <Input
                  data-testid="admin-image-url-input"
                  placeholder="Paste image URL"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addImageUrl())}
                  className="bg-[#05050A] border-white/15 focus:border-[#DC143C] text-white flex-1"
                />
                <Button
                  type="button"
                  data-testid="admin-add-image-btn"
                  onClick={addImageUrl}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 shrink-0"
                >
                  Add
                </Button>
              </div>
              {form.images.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative group w-16 h-16">
                      <img src={img} alt="" className="w-full h-full object-cover rounded border border-white/10" />
                      <button
                        onClick={() => removeImage(i)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="admin-product-featured"
                onClick={() => setForm({ ...form, featured: !form.featured })}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  form.featured ? "bg-[#DC143C]/20 text-[#DC143C] border border-[#DC143C]/40" : "bg-white/5 text-[#9CA3AF] border border-white/10"
                }`}
              >
                {form.featured ? <Star size={14} /> : <StarOff size={14} />}
                {form.featured ? "Featured" : "Not Featured"}
              </button>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setFormOpen(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button
              data-testid="admin-save-product-btn"
              onClick={handleSubmitProduct}
              className="bg-[#DC143C] text-white hover:bg-[#B01030]"
            >
              {editingProduct ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
